import React, { useState, useEffect } from "react";
import "@fontsource/outfit";
import "@fontsource/outfit/900.css";

const MAX_TOTAL_SCORE = 90;

const CircleVisualizerV3 = () => {
  const categories = ["Person", "Process", "System"];
  const colors = ["#D32F2F", "#1976D2", "#F9A825"];
  const questions = {
    Person: [
      "How much do you feel that the finished product embodies your values, personality, and identity?",
      "How often do you think about the product?",
      "Do you receive a lot of external recognition for your work?",
    ],
    Process: [
      "How much control did you have over the creative decisions that shaped the final product?",
      "How intentional were you about the creative decisions that you made?",
      "How much physical and/or mental effort did you put into this work?",
    ],
    System: [
      "How closely did you physically work with final product?",
      "How closely did you conceptually work with the final product?",
      "How independent were you during the creative process?",
    ],
  };

  const [scores, setScores] = useState({
    person: [1, 1, 1],
    process: [1, 1, 1],
    system: [1, 1, 1],
  });
  const [thresholdPercent, setThresholdPercent] = useState(50);
  const [maxRadius, setMaxRadius] = useState(140);
  const [isVertical, setIsVertical] = useState(false);

  useEffect(() => {
    const updateLayout = () => {
      const shouldStack = window.innerWidth < 1000;
      setIsVertical(shouldStack);
      const usableHeight = window.innerHeight - 120; // extra compact to allow full usage
      const usableWidth = shouldStack ? window.innerWidth : window.innerWidth * 0.5;
      const minSide = Math.min(usableHeight, usableWidth);
      setMaxRadius(shouldStack ? 120 : 0.46 * minSide); // slightly bigger
    };
    updateLayout();
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, []);

  const getSum = (arr) => arr.reduce((a, b) => a + b, 0);
  const personSum = getSum(scores.person);
  const processSum = getSum(scores.process);
  const systemSum = getSum(scores.system);

  const scoreArray = [
    { key: "person", value: personSum },
    { key: "process", value: processSum },
    { key: "system", value: systemSum },
  ].sort((a, b) => b.value - a.value);

  const maxArea = Math.PI * maxRadius * maxRadius;
  const totalScore = personSum + processSum + systemSum;
  const totalArea = (totalScore / MAX_TOTAL_SCORE) * maxArea;

  const firstArea = totalArea;
  const secondArea = (scoreArray[1].value / scoreArray[0].value) * firstArea || 0;
  const thirdArea = (scoreArray[2].value / scoreArray[1].value) * secondArea || 0;

  const areaMap = {
    [scoreArray[0].key]: firstArea,
    [scoreArray[1].key]: secondArea,
    [scoreArray[2].key]: thirdArea,
  };

  const getRadiusFromArea = (area) => Math.sqrt(area / Math.PI);
  const radii = [
    getRadiusFromArea(areaMap.person),
    getRadiusFromArea(areaMap.process),
    getRadiusFromArea(areaMap.system),
  ];

  const dashedRadius = (thresholdPercent / 100) * maxRadius;

  const handleChange = (group, questionIdx, value) => {
    const updated = { ...scores };
    updated[group][questionIdx] = value;
    setScores(updated);
  };

  return (
    <div
      className={`w-full ${isVertical ? "h-auto overflow-y-auto" : "h-screen overflow-hidden"} p-4 flex flex-col justify-center`}
      style={{
        fontFamily: "'Outfit', sans-serif",
        backgroundColor: "#FAF6EF",
        color: "#2B2B2B",
      }}
    >
      <h1
        className="text-5xl md:text-6xl text-[#D32F2F] text-center mb-3 uppercase"
        style={{
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 900,
          letterSpacing: "0.05em",
          textShadow: "1px 1px 0px #FAF6EF, 2px 2px 0px #E0E0E0",
        }}
      >
        Creative Ownership Test
      </h1>

      <div className={`flex ${isVertical ? "flex-col items-center" : "flex-row justify-between items-center"} w-full grow gap-4`}>
        <div
          className={`flex flex-col justify-center ${isVertical ? "w-full items-center" : "w-[40%]"}`}
          style={{ height: isVertical ? "auto" : "100%" }}
        >
          <div className="flex flex-col gap-3 w-full">
            {categories.map((category, catIdx) => (
              <div
                key={category}
                className="border border-[#E0E0E0] bg-[#FAF6EF] w-full"
              >
                <h2
                  className="px-4 py-1 text-center font-semibold text-sm uppercase"
                  style={{ color: colors[catIdx] }}
                >
                  {category}
                </h2>
                {questions[category].map((label, qIdx) => (
                  <div
                    key={qIdx}
                    className="grid grid-cols-[1fr_auto] border-t border-[#F0F0F0] text-xs px-3 py-1 items-center gap-1"
                  >
                    <div className="pr-2">{label}</div>
                    <div className="flex flex-nowrap gap-[2px]">
                      {[...Array(10)].map((_, i) => (
                        <label key={i} className="cursor-pointer">
                          <input
                            type="radio"
                            name={`${category}-q${qIdx}`}
                            value={i + 1}
                            checked={scores[category.toLowerCase()][qIdx] === i + 1}
                            onChange={() => handleChange(category.toLowerCase(), qIdx, i + 1)}
                            className="appearance-none w-3 h-3 rounded-full border border-[#4B4B4B] checked:bg-[#4B4B4B] transition duration-150"
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="text-xs pt-4 w-full">
            <label className="block mb-1 font-semibold text-center">Threshold</label>
            <input
              type="range"
              min="0"
              max="100"
              value={thresholdPercent}
              onChange={(e) => setThresholdPercent(Number(e.target.value))}
              className="w-full accent-[#4B4B4B]"
            />
          </div>
        </div>

        <div className={`flex flex-col items-center justify-center ${isVertical ? "mt-4" : "w-[60%]"}`}> {/* center circles better */}
          <div
            className="relative"
            style={{ width: maxRadius * 2, height: maxRadius * 2 }}
          >
            {radii.map((radius, idx) => (
              <div
                key={idx}
                className="absolute rounded-full border transition-all duration-300"
                style={{
                  width: radius * 2,
                  height: radius * 2,
                  borderColor: colors[idx],
                  borderWidth: 2,
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            ))}
            <div
              className="absolute rounded-full border border-dashed"
              style={{
                width: dashedRadius * 2,
                height: dashedRadius * 2,
                borderColor: "#BDBDBD",
                boxShadow: "0 0 12px 4px rgba(0, 0, 0, 0.05)",
                borderWidth: 2,
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            />
          </div>

          <button
            className={`mt-4 px-6 py-2 border border-[#4B4B4B] font-semibold rounded-full transition-all tracking-wide text-sm ${
              radii.some((r) => r > dashedRadius)
                ? "bg-[#4B4B4B] text-white shadow-md"
                : "bg-transparent text-[#4B4B4B] hover:bg-[#F3F3F3]"
            }`}
          >
            Ownership
          </button>
        </div>
      </div>
    </div>
  );
};

export default CircleVisualizerV3;
